import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import BulkActionsBar from '../../Elements/BulkActionsBar';
import CoarseSafeSelect from '../../Elements/CoarseSafeSelect';
import { UserContext } from '../../../context/UserContext';
import CustomerModal from './CustomerModal';
import PurchaseModal from '../../Elements/PurchaseModal';
import { balanceListLabel, balanceToneClass } from '../../../lib/helpers/balance';
import AddCashModal from '../../Elements/AddCashModal';
import SociosModal from './SociosModal';
import ClientDetailModal from './ClientDetailModal';
import SectionHeader from '../../Elements/SectionHeader';
import Button from '../../Elements/Button';
import MenuFlotante from '../../Elements/MenuFlotante';
import { TableHead, Th, Tr, Td } from '../../Elements/DataTable';
import EmptyState from '../../Elements/EmptyState';
import useConfirmar from '../../Elements/useConfirmar';
import ConfirmDialog from '../../Elements/ConfirmDialog';
import CaducadoChip from '../../Elements/CaducadoChip';
import { estaCaducado } from '../../../lib/helpers/socioEstado';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaDeleteLeft } from "react-icons/fa6";
import { HiOutlineSearch, HiDotsVertical, HiOutlineEye, HiOutlineCreditCard, HiOutlineTrash, HiOutlineQrcode } from "react-icons/hi";
import axios from 'axios';
import { getImageCacheKey, getImageUrl, socioNum } from '../../../lib/helpers';
import QRModal from './QRModal';
import MemberPhoto, { FOTO_DE_FILA, FOTO_DE_TARJETA } from '../../Elements/MemberPhoto';
import { useInVenueUsers } from '../../../context/useInVenueUsers';

function getClientProfileImagePath(profile) {
  return profile?.documents_path || profile?.image_path || null;
}

const SORT_FILTER_OPTIONS = [
  { value: 'membership_id:desc', label: 'Nº socio (mayor → menor)' },
  { value: 'membership_id:asc', label: 'Nº socio (menor → mayor)' },
  { value: 'credit_balance:desc', label: 'Saldo (mayor → menor)' },
  { value: 'credit_balance:asc', label: 'Saldo (menor → mayor)' },
  { value: 'enrollment_date:desc', label: 'Inscripción (recientes)' },
  { value: 'enrollment_date:asc', label: 'Inscripción (antiguas)' },
  { value: 'expiration_date:asc', label: 'Caducidad (próximas)' },
  { value: 'expiration_date:desc', label: 'Caducidad (lejanas)' },
];


const STATUS_FILTER_OPTIONS_DESKTOP = [
  { value: 'all', label: 'Estado: Todos' },
  { value: 'active', label: 'Activos' },
  { value: 'expired', label: 'Caducados' },
];


const applySortValue = (value, setSortBy, setSortDir) => {
  const [field, dir] = value.split(':');
  setSortBy(field);
  setSortDir(dir);
};

function uploadFileWithProgress({ url, file, method = 'PUT', headers = {}, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      reject(new Error(`Upload failed (${xhr.status})`));
    });
    xhr.addEventListener('error', () => reject(new Error('Error de red al subir el archivo')));
    xhr.open(method, url);
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.send(file);
  });
}

function downloadSociosCSV(venueID, statusFilter, token) {
  const url = `/v1/clients/export/${venueID}?status_filter=${statusFilter}`;
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  // We need auth — use fetch + blob so the Authorization header is sent
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(res => {
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `socios-${statusFilter}.csv`;
      return res.blob().then(blob => ({ blob, filename }));
    })
    .then(({ blob, filename }) => {
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(blobUrl);
    })
    .catch(err => alert(`Error al exportar: ${err.message}`));
}

function applyLogFilter(rows, filter) {
  if (!rows) return [];
  return rows.filter(r => {
    if (filter === 'all') return true;
    if (filter === 'created') return r.status === 'created' && (!r.warnings || r.warnings.length === 0);
    if (filter === 'updated') return r.status === 'updated';
    if (filter === 'warning') return (r.warnings && r.warnings.length > 0) || r.status === 'skipped_cap';
    if (filter === 'error') return r.status === 'error';
    return true;
  });
}

function downloadFilteredCSV(importLog, filter) {
  const rows = applyLogFilter(importLog.rows, filter);
  const filterLabel = { all: 'todas', created: 'creados', updated: 'actualizados', warning: 'avisos', error: 'errores' }[filter] || filter;

  const headers = ['Fila', 'Estado', 'Nombre', 'DNI_Original', 'Avisos', 'Error'];
  const escape = v => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s}"` : s;
  };

  const lines = [
    headers.join(','),
    ...rows.map(r => [
      r.row ?? '',
      r.status ?? '',
      r.name ?? '',
      r.dni_original ?? '',
      (r.warnings || []).join(' | '),
      r.error ?? '',
    ].map(escape).join(',')),
  ];

  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `import-log-${importLog.log_id}-${filterLabel}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function normalizeImportLogPayload(data, fallbackId) {
  if (!data || data.error) return data;
  return {
    ...data,
    log_id: data.log_id || fallbackId,
  };
}

export default function Customers() {
  const [token] = useContext(UserContext);
  const [, , , venueID] = useContext(UserContext);
  const [, , , , , , , accessPrivileges] = useContext(UserContext);
  // Exportar el padrón e importar ZIP: solo owner y superadmin (Yago, 13-09-2026).
  // El API lo exige igual (owner_privileges en /v1/clients/export y /import-jobs*):
  // aquí solo se esconden los botones al staff.
  const puedeExportarImportar = accessPrivileges === 'owner' || accessPrivileges === 'superadmin';
  const [inputFiltro, setInputFiltro] = useState("");

  const [users, setUsers] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [user_id, setUser_id] = useState(null);
  const [activeModal, setActiveModal] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [purchaseModal, setPurchaseModal] = useState(false);
  const [creditModal, setCreditModal] = useState(false);
  const [modalEditSocios, setModalEditSocios] = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  // Puerta ÚNICA a la ficha (fix review 16-ago): cuatro sitios la abrían con el
  // mismo par de setters inlineado — así el próximo cambio se hace UNA vez.
  const openClientDetail = useCallback((user) => {
    setSelectedClient(user);
    setDetailModal(true);
  }, []);
  const [confirmDelete, setConfirmDelete] = useState(null); // socio a eliminar (diálogo Figma)
  const [actionMenu, setActionMenu] = useState(null); // { user, anchorEl } menú ⋮ de fila
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  // ConfirmDialog y no `window.confirm`: el nativo se traga en las tablets del
  // club y la acción moría sin decir nada (auditoría de UI, UI-003).
  const { confirmar, dialogo: dialogoConfirmar } = useConfirmar();

  // Import log / job tracking
  const [lastImportLogId, setLastImportLogId] = useState(() => localStorage.getItem('lastImportLogId') || null);
  const [importLog, setImportLog] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [loadingLog, setLoadingLog] = useState(false);
  const [logFilter, setLogFilter] = useState('all'); // 'all' | 'warning' | 'error' | 'created'
  const [sociosCap, setSociosCap] = useState(null);
  const [showImportProgressModal, setShowImportProgressModal] = useState(false);
  const [importPhase, setImportPhase] = useState('idle'); // idle | uploading | processing | done | error
  const [importJobStatus, setImportJobStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importStatusMessage, setImportStatusMessage] = useState('');
  const pollTimerRef = useRef(null);

  const PAGE_SIZE = 30;
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(null); // total que coincide con filtros (null = desconocido / búsqueda)
  const [loadingUsers, setLoadingUsers] = useState(false);
  // Orden y filtros (como en danimateu). Por defecto: Nº socio descendente → último socio primero.
  const [sortBy, setSortBy] = useState('membership_id'); // membership_id | credit_balance | enrollment_date | expiration_date
  const [sortDir, setSortDir] = useState('desc');        // asc | desc
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | expired
  const [gotoPageInput, setGotoPageInput] = useState(''); // input "ir a página"
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();   // solo lectura: el ?userId= se conserva
  const { usersInVenue } = useInVenueUsers();
  const searchTimerRef = useRef(null);
  const inputFiltroRef = useRef("");
  inputFiltroRef.current = inputFiltro;

  const importHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-venue-id': venueID,
  }), [token, venueID]);

  const clearImportPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearImportPoll(), [clearImportPoll]);

  useEffect(() => {
    if (!token || !venueID) {
      setSociosCap(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/v1/venues/${venueID}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && (res.data.max_socios != null || res.data.socios_count != null)) {
          setSociosCap({
            socios_count: res.data.socios_count ?? 0,
            max_socios: res.data.max_socios ?? null,
          });
        }
      } catch {
        if (!cancelled) setSociosCap(null);
      }
    })();
    return () => { cancelled = true; };
  }, [token, venueID]);

  const refreshSociosCap = useCallback(async () => {
    if (!token || !venueID) return;
    try {
      const res = await axios.get(`/v1/venues/${venueID}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.max_socios != null || res.data.socios_count != null) {
        setSociosCap({
          socios_count: res.data.socios_count ?? 0,
          max_socios: res.data.max_socios ?? null,
        });
      }
    } catch {
      /* keep previous cap state */
    }
  }, [token, venueID]);

  const atSociosCap = sociosCap?.max_socios != null && sociosCap.socios_count >= sociosCap.max_socios;
  const sociosCapRemaining = sociosCap?.max_socios != null
    ? Math.max(0, sociosCap.max_socios - sociosCap.socios_count)
    : null;

  const fetchPage = useCallback(async (pageIndex, searchTerm = "") => {
    if (!token || !venueID) return;
    setLoadingUsers(true);
    try {
      let list;
      let total = null;
      const skip = pageIndex * PAGE_SIZE;
      if (searchTerm.trim()) {
        // La búsqueda usa su propio endpoint (sin orden/estado ni total): paginación simple anterior/siguiente.
        const res = await axios.get(`/v1/clients/search/${venueID}`, {
          params: { q: searchTerm.trim(), skip, limit: PAGE_SIZE },
          headers: { Authorization: `Bearer ${token}` },
        });
        list = Array.isArray(res.data) ? res.data : [];
      } else {
        const res = await axios.get('/v1/clients', {
          params: {
            venue_id: venueID,
            skip,
            limit: PAGE_SIZE,
            sort_by: sortBy,
            sort_dir: sortDir,
            status_filter: statusFilter,
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        list = Array.isArray(res.data) ? res.data : [];
        const parsed = parseInt(res.headers['x-total-count'], 10);
        total = Number.isFinite(parsed) ? parsed : null;
      }
      setUsers(list);
      setTotalCount(total);
      setHasMore(list.length === PAGE_SIZE);
      setErrorMessage("");
    } catch (error) {
      console.error("Error fetching clients:", error);
      setErrorMessage(error.response?.data?.detail || "Error al cargar clientes");
      setUsers([]);
      setTotalCount(null);
    } finally {
      setLoadingUsers(false);
    }
  }, [token, venueID, sortBy, sortDir, statusFilter]);

  // Carga inicial + recarga al cambiar orden/estado/venue (conserva el término de búsqueda actual).
  useEffect(() => {
    setPage(0);
    fetchPage(0, inputFiltroRef.current);
  }, [fetchPage]);

  useEffect(() => {
    setSelectedUserIds((prev) => prev.filter((id) => users.some((user) => String(user.id) === String(id))));
  }, [users]);

  const handleInput = (e) => {
    const val = e.target.value;
    setInputFiltro(val);
    // Debounce server-side search
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(0);
      fetchPage(0, val);
    }, 350);
  };

  // Enlace profundo ?userId= → abre la ficha.
  //
  // El parámetro SE MANTIENE en la URL. Antes se borraba con setSearchParams({})
  // justo después de abrir, y eso rompía dos cosas: al recargar se perdía el
  // socio, y el enlace no se podía compartir ni guardar — que es exactamente
  // para lo que sirve un enlace profundo. La ficha se cierra con su botón, no
  // vaciando la barra de direcciones.
  //
  // El guard `abiertoRef` evita que el efecto reabra la ficha si el staff la
  // cierra sin cambiar la URL: sin él, el parámetro seguiría ahí y el modal
  // volvería a saltar en el siguiente render.
  const deepLinkRef = useRef(null);
  useEffect(() => {
    const userIdParam = searchParams.get('userId');
    if (!userIdParam || users.length === 0) return;
    if (deepLinkRef.current === userIdParam) return;
    const user = users.find(u => u.id === userIdParam || u.id?.toString() === userIdParam);
    if (user) {
      deepLinkRef.current = userIdParam;
      openClientDetail(user);
    }
  }, [searchParams, users, openClientDetail]);

  const mostrarUsuarios = users;
  const paginaActual = page + 1;
  // En modo búsqueda no hay total → paginación simple. En modo listado usamos X-Total-Count.
  const isSearching = inputFiltro.trim() !== "";

  // Limpia la búsqueda y recarga la primera página sin esperar al debounce:
  // lo usa el estado vacío de "sin resultados", donde el staff ya ha decidido
  // que quiere volver a la lista entera.
  const clearFiltro = useCallback(() => {
    clearTimeout(searchTimerRef.current);
    setInputFiltro("");
    setPage(0);
    fetchPage(0, "");
  }, [fetchPage]);

  // Dos estados vacíos distintos, que antes compartían una sola línea de texto:
  // no hay socios todavía (onboarding con los tres caminos) frente a la búsqueda
  // no encuentra nada (conserva lo tecleado y ofrece limpiar).
  const renderSociosEmptyState = (className = '') => (
    isSearching ? (
      <EmptyState
        variant="noResults"
        className={className}
        title={`Sin resultados para «${inputFiltro.trim()}»`}
        description="Puedes buscar por número de socio, DNI, apellido, nombre o email."
        hint="Comprueba si sobra algún espacio o si el DNI lleva la letra."
        actions={[
          { label: 'Limpiar búsqueda', onClick: clearFiltro, tone: 'primary' },
        ]}
      />
    ) : (
      <EmptyState
        variant="empty"
        className={className}
        title="Todavía no hay socios"
        description="Puedes darlos de alta uno a uno, dejar que se preinscriban ellos con el QR, o importar los que ya tengas."
        actions={[
          { label: 'Nuevo socio', onClick: () => setActiveModal(true), tone: 'primary' },
          { label: 'QR de preinscripción', onClick: () => setQrModalOpen(true) },
          ...(puedeExportarImportar
            ? [{ label: 'Subir ZIP', onClick: () => fileInputRef.current?.click() }]
            : []),
        ]}
      />
    )
  );
  const totalPages = (!isSearching && totalCount != null)
    ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
    : null;

  const goToPage = (oneBasedPage) => {
    let target = oneBasedPage;
    if (totalPages != null) target = Math.min(Math.max(1, target), totalPages);
    else target = Math.max(1, target);
    const next = target - 1;
    setPage(next);
    fetchPage(next, inputFiltro);
  };
  const setPaginaActual = (fn) => {
    const target = (typeof fn === 'function' ? fn(paginaActual) : fn);
    goToPage(target);
  };

  // Ventana de números de página: 1 … (actual-1) actual (actual+1) … última
  const pageWindow = (() => {
    if (totalPages == null || totalPages <= 1) return [];
    const pages = new Set([1, totalPages, paginaActual, paginaActual - 1, paginaActual + 1]);
    const sorted = [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const p of sorted) {
      if (prev && p - prev > 1) out.push('…');
      out.push(p);
      prev = p;
    }
    return out;
  })();

  const handleGotoSubmit = (e) => {
    e.preventDefault();
    const n = parseInt(gotoPageInput, 10);
    if (Number.isFinite(n)) goToPage(n);
    setGotoPageInput('');
  };

  const performDelete = async (id) => {
    try {
      const res = await fetch(`/v1/users/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ detail: 'Error al eliminar cliente' }));
        throw new Error(errorData.detail || `Error ${res.status}`);
      }

      await fetchPage(page, inputFiltro);
      setErrorMessage("");
    } catch (error) {
      console.error("Error deleting client:", error);
      setErrorMessage(error.message || "Error al eliminar cliente");
    }
  };

  // Abre el diálogo de confirmación (Figma) en vez de window.confirm
  const handleDelete = (id) => {
    const u = users.find((x) => String(x.id) === String(id));
    setConfirmDelete(u || { id });
  };

  const toggleSelectedUser = (id) => {
    setSelectedUserIds((prev) =>
      prev.some((currentId) => String(currentId) === String(id))
        ? prev.filter((currentId) => String(currentId) !== String(id))
        : [...prev, id]
    );
  };

  const clearSelectedUsers = () => setSelectedUserIds([]);

  const selectVisibleUsers = () => {
    setSelectedUserIds(mostrarUsuarios.map((user) => user.id));
  };

  const deleteUserById = async (id) => {
    const res = await fetch(`/v1/users/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({ detail: 'Error al eliminar cliente' }));
      throw new Error(errorData.detail || `Error ${res.status}`);
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedUserIds.length) return;
    const n = selectedUserIds.length;
    const eliminar = await confirmar({
      title: n === 1 ? 'Eliminar 1 socio' : `Eliminar ${n} socios`,
      description: n === 1
        ? 'Se eliminará el socio seleccionado, con sus entradas y su historial.'
        : `Se eliminarán los ${n} socios seleccionados, con sus entradas y su historial.`,
      note: 'Esta acción no tiene vuelta atrás.',
      confirmLabel: n === 1 ? 'Eliminar socio' : 'Eliminar socios',
      tone: 'danger',
    });
    if (!eliminar) return;

    setIsBulkDeleting(true);
    const results = await Promise.allSettled(selectedUserIds.map(deleteUserById));
    const failedCount = results.filter((result) => result.status === 'rejected').length;
    await fetchPage(page, inputFiltro);
    setIsBulkDeleting(false);
    setSelectedUserIds([]);

    if (failedCount > 0) {
      setErrorMessage(`Se eliminaron ${selectedUserIds.length - failedCount} socios y ${failedCount} fallaron.`);
      return;
    }

    setErrorMessage("");
  };

  // Función para crear entrada automáticamente cuando se crea un nuevo socio desde la página de socios
  const handleAutoEntryForNewClient = async (newClient) => {
    if (!newClient || !venueID || !token) {
      console.log("⚠️ No se puede crear entrada: faltan datos", { newClient, venueID, token });
      return;
    }

    try {
      // Crear la entrada en el backend para que el socio aparezca en "socios en sala"
      await axios.post(
        "/v1/entries",
        { user_id: newClient.id, venue_id: venueID },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("✅ Nuevo socio agregado automáticamente a 'Socios en sala' en la página de entrada");
    } catch (error) {
      console.error("Error creando entrada automática para nuevo socio:", error);
      // No bloqueamos el flujo si falla la entrada automática, solo mostramos el error en consola
    } finally {
      // Redirigir a la página Entradas tras crear el socio
      navigate("/control");
    }
  };

  // Obtener datos del cliente para edición
  const clientToEdit = user_id ? users.find(u => u.id === user_id) : null;

  const handleClientUpdated = useCallback(async (updatedClient) => {
    if (!updatedClient?.id) {
      await fetchPage(page, inputFiltro);
      return;
    }
    setUsers((prev) => prev.map((user) => (String(user.id) === String(updatedClient.id) ? updatedClient : user)));
    setSelectedClient((prev) => (String(prev?.id) === String(updatedClient.id) ? updatedClient : prev));
  }, [fetchPage, page, inputFiltro]);

  // La colocación (portal, medir, flip) la hace MenuFlotante; aquí solo se guarda el botón.

  const closeActionMenu = useCallback(() => {
    setActionMenu(null);
  }, []);

  // Debug: Ver qué datos tiene el cliente cuando se edita
  useEffect(() => {
    if (user_id && clientToEdit) {
      console.log('🔍 Customers - Client to edit:', {
        id: user_id,
        clientToEdit,
        profile: clientToEdit.profile,
        consumption_type: clientToEdit.profile?.consumption_type,
        hasConsumptionType: !!clientToEdit.profile?.consumption_type,
        ludenos: clientToEdit.profile?.consumption_type?.ludenos,
        terap: clientToEdit.profile?.consumption_type?.terap
      });
    }
  }, [user_id, clientToEdit]);

  // Bulk Upload Handler
  const fileInputRef = useRef(null);

  const fetchImportJobStatus = useCallback(async (jobId) => {
    const res = await axios.get(`/v1/clients/import-jobs/${jobId}`, {
      headers: importHeaders(),
    });
    return res.data;
  }, [importHeaders]);

  const startImportJobPolling = useCallback((jobId) => {
    clearImportPoll();

    const pollOnce = async () => {
      try {
        const status = await fetchImportJobStatus(jobId);
        setImportJobStatus(status);

        if (status.status === 'completed') {
          clearImportPoll();
          setImportPhase('done');
          let completionMessage =
            `Importación completada: ${status.created_count} socios procesados` +
            (status.error_count ? `, ${status.error_count} errores` : '') + '.';
          try {
            const logRes = await axios.get(`/v1/clients/import-jobs/${jobId}/log`, {
              headers: importHeaders(),
            });
            const capMsg = logRes.data?.cap_warning?.message;
            if (capMsg) completionMessage += ` ⚠️ ${capMsg}`;
            const dniCount = logRes.data?.dni_queued;
            if (dniCount > 0) completionMessage += ` 🪪 ${dniCount} DNIs subidos.`;
          } catch {
            /* log may not be ready yet */
          }
          setImportStatusMessage(completionMessage);
          fetchPage(0, inputFiltro);
          refreshSociosCap();
        } else if (status.status === 'failed') {
          clearImportPoll();
          setImportPhase('error');
          setImportStatusMessage(status.error_message || 'La importación falló.');
        } else if (status.status === 'cancelled') {
          clearImportPoll();
          setImportPhase('error');
          setImportStatusMessage('Importación cancelada.');
        } else {
          const processed = (status.created_count || 0) + (status.error_count || 0);
          const totalHint = status.total_rows ? ` / ${status.total_rows}` : '';
          setImportStatusMessage(`Procesando socios… ${processed}${totalHint}`);
        }
      } catch (pollError) {
        console.warn('Import job poll failed:', pollError);
      }
    };

    pollOnce();
    pollTimerRef.current = setInterval(pollOnce, 3000);
  }, [clearImportPoll, fetchImportJobStatus, fetchPage, importHeaders, inputFiltro, refreshSociosCap]);

  const runLegacyBulkUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post('/v1/clients/bulk-upload', formData, {
      headers: importHeaders(),
      timeout: 10 * 60 * 1000,
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
        }
      },
    });

    if (response.data.log_id) {
      setLastImportLogId(response.data.log_id);
      localStorage.setItem('lastImportLogId', response.data.log_id);
    }

    const previewErrors = (response.data.errors || []).slice(0, 5);
    const summary = [
      '✅ Carga masiva completada.',
      `Creados: ${response.data.created_count}`,
      `Avisos/Errores: ${(response.data.errors || []).length}`,
    ];
    if (response.data.cap_warning?.message) {
      summary.push('', `⚠️ ${response.data.cap_warning.message}`);
    }
    if (previewErrors.length > 0) {
      summary.push('', 'Primeros avisos:');
      summary.push(...previewErrors);
      if (response.data.errors.length > previewErrors.length) {
        summary.push(`... y ${response.data.errors.length - previewErrors.length} más`);
      }
    }
    if (response.data.log_id) {
      summary.push('', '📋 Pulsa "Ver Log Import" para el informe completo.');
    }
    alert(summary.join('\n'));
    fetchPage(0, inputFiltro);
    refreshSociosCap();
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.zip')) {
      alert('Por favor, sube un archivo .zip');
      event.target.value = null;
      return;
    }

    if (atSociosCap) {
      alert('Límite de socios alcanzado. Mejora tu plan en Configuración antes de importar más socios.');
      event.target.value = null;
      return;
    }

    if (sociosCapRemaining != null && sociosCapRemaining < 50) {
      const proceed = await confirmar({
        title: 'Quedan pocas plazas',
        description:
          `Tu plan permite ${sociosCap.max_socios} socios y ya tienes ${sociosCap.socios_count}. ` +
          `Solo quedan ${sociosCapRemaining} plaza(s): la importación se detendrá al alcanzar el límite.`,
        confirmLabel: 'Importar igualmente',
      });
      if (!proceed) {
        event.target.value = null;
        return;
      }
    }

    try {
      setErrorMessage('');
      clearImportPoll();
      setShowImportProgressModal(true);
      setImportPhase('uploading');
      setUploadProgress(0);
      setImportJobStatus(null);
      setImportStatusMessage('Preparando importación…');

      let createRes;
      try {
        createRes = await axios.post('/v1/clients/import-jobs', {
          filename: file.name,
          size_bytes: file.size,
          content_type: 'application/zip',
        }, { headers: importHeaders() });
      } catch (createError) {
        if (createError.response?.status === 404) {
          setShowImportProgressModal(false);
          await runLegacyBulkUpload(file);
          return;
        }
        throw createError;
      }

      const { job_id: jobId, upload } = createRes.data;
      setLastImportLogId(jobId);
      localStorage.setItem('lastImportLogId', jobId);
      setImportStatusMessage('Subiendo archivo…');

      if (upload.mode === 's3') {
        await uploadFileWithProgress({
          url: upload.url,
          file,
          method: upload.method || 'PUT',
          headers: upload.headers || { 'Content-Type': 'application/zip' },
          onProgress: setUploadProgress,
        });
        setUploadProgress(100);
        await axios.post(`/v1/clients/import-jobs/${jobId}/confirm-upload`, null, {
          headers: importHeaders(),
        });
      } else {
        const formData = new FormData();
        formData.append('file', file);
        // Do NOT set Content-Type manually — axios must send multipart boundary.
        await axios.post(upload.url, formData, {
          headers: importHeaders(),
          timeout: 10 * 60 * 1000,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
            }
          },
        });
        setUploadProgress(100);
      }

      setImportPhase('processing');
      setImportStatusMessage('Archivo recibido. Procesando socios en segundo plano…');
      try {
        await axios.post(`/v1/clients/import-jobs/${jobId}/start`, null, {
          headers: importHeaders(),
        });
      } catch (startErr) {
        if (startErr.response?.status === 409) {
          // Another import is already running for this venue
          setImportPhase('error');
          setImportStatusMessage(startErr.response.data?.detail || 'Ya hay una importación en curso. Espera a que termine o cancélala.');
          return;
        }
        throw startErr;
      }

      startImportJobPolling(jobId);
    } catch (error) {
      console.error('Bulk upload error:', error);
      clearImportPoll();
      setImportPhase('error');
      const msg = error.response?.data?.detail || error.message || 'Error en la carga masiva';
      setImportStatusMessage(msg);
      setErrorMessage(msg);
    } finally {
      event.target.value = null;
    }
  };

  const openImportLog = async () => {
    if (!lastImportLogId) return;
    setLoadingLog(true);
    setShowLogModal(true);
    setImportLog(null);
    setLogFilter('all');
    try {
      const res = await axios.get(`/v1/clients/import-jobs/${lastImportLogId}/log`, {
        headers: importHeaders(),
      });
      setImportLog(normalizeImportLogPayload(res.data, lastImportLogId));
    } catch (e) {
      if (e.response?.status === 404) {
        setImportLog({ error: 'El log de esta importación no está disponible. Puede que pertenezca a una sesión anterior o que el servidor haya sido reiniciado. Realiza una nueva importación para ver el log completo.' });
      } else {
        setImportLog({ error: 'No se pudo cargar el log de importación.' });
      }
    } finally {
      setLoadingLog(false);
    }
  };

  const closeImportProgressModal = async () => {
    if (importPhase === 'processing') {
      const leave = await confirmar({
        title: 'Cerrar el panel',
        description: 'La importación sigue en segundo plano. Puedes ver el resultado con «Ver Log Import».',
        confirmLabel: 'Cerrar',
      });
      if (!leave) return;
      clearImportPoll();
    }
    setShowImportProgressModal(false);
  };

  const cancelCurrentImport = async () => {
    const jobId = lastImportLogId;
    if (!jobId) return;
    const confirmed = await confirmar({
      title: 'Cancelar la importación',
      description: 'Los socios ya importados se quedan; el resto del fichero no se procesará.',
      confirmLabel: 'Cancelar la importación',
      cancelLabel: 'Seguir importando',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await axios.post(`/v1/clients/import-jobs/${jobId}/cancel`, {}, { headers: importHeaders() });
      clearImportPoll();
      setImportPhase('error');
      setImportStatusMessage('Importación cancelada.');
    } catch (e) {
      alert(e.response?.data?.detail || 'No se pudo cancelar la importación.');
    }
  };

  // Una sola cifra de socios para las dos cabeceras (escritorio y compacta).
  // Compacta: solo la cifra. Las plazas ensanchaban el título y el botón
  // «Nuevo socio» se salía de la tarjeta en el iPad vertical (10-09-2026).
  const resumenSociosCorto =
    sociosCap?.max_socios != null
      ? `${sociosCap.socios_count ?? totalCount ?? 0} / ${sociosCap.max_socios} socios`
      : (totalCount != null ? `${totalCount} socio${totalCount !== 1 ? 's' : ''}` : null);
  const resumenSocios = (() => {
    if (sociosCap?.max_socios == null) {
      return totalCount != null ? `${totalCount} socio${totalCount !== 1 ? 's' : ''}` : null;
    }
    const base = `${sociosCap.socios_count ?? totalCount ?? 0} / ${sociosCap.max_socios} socios`;
    if (atSociosCap || sociosCapRemaining == null) return base;
    return `${base} · ${sociosCapRemaining} plaza${sociosCapRemaining !== 1 ? 's' : ''} disponible${sociosCapRemaining !== 1 ? 's' : ''}`;
  })();

  return (
    <div className="p-3 sm:p-4 space-y-3 sm:space-y-4 max-w-full">
      {/* Desktop: barra normal */}
      {/* Desktop: barra unificada (Figma) */}
      {/* La cifra de socios va UNA vez, bajo el título (decidido 10-09-2026): antes
          iba como píldora junto al título y otra vez en la tarjeta del cupo. */}
      <SectionHeader
        title="Listado Socios"
        subtitle={resumenSocios}
        sticky={false}
        className="hidden xl:flex xl:items-start"
        actionsClassName="min-w-0 flex-1 flex-col items-stretch"
      >
        {/* Fila 1: el buscador entre el título y «Nuevo socio» (decidido 10-09-2026,
            Yago, mirándolo en el iPad). El buscador crece; el botón no cede. */}
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
            <input
              value={inputFiltro}
              onChange={handleInput}
              placeholder="Buscar socio"
              className="h-9 w-full rounded-sm border border-[var(--border)] pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)]"
            />
          </div>
          {/* QR de preinscripción: al lado de «Nuevo socio», que son las dos formas
              de dar de alta. Y así la fila 2 cabe en la tablet de 1280. */}
          <Button
            variant="outline"
            onClick={() => setQrModalOpen(true)}
            title="QR Preinscripción"
            aria-label="QR Preinscripción"
            className="shrink-0 px-3"
          >
            <HiOutlineQrcode className="text-base" /> QR
          </Button>
          <Button onClick={() => setActiveModal(true)} className="shrink-0">
            Nuevo socio
          </Button>
        </div>

        {/* Fila 2: filtros a la izquierda; Subir ZIP · Exportar a la derecha. Van
            primero en el DOM y la fila es `row-reverse` para que, si alguna vez no
            caben, queden ENCIMA de los filtros y nunca debajo. */}
        <div className="flex flex-row-reverse flex-wrap items-center justify-between gap-2">
        {puedeExportarImportar && (
        <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="px-3">
          Subir ZIP
        </Button>
        <Button
          variant="outline"
          onClick={() => downloadSociosCSV(venueID, statusFilter, token)}
          disabled={isSearching}
          title={isSearching ? 'Sal de la búsqueda para exportar por estado' : 'Descargar CSV de los socios filtrados'}
          className="px-3"
        >
          Exportar
        </Button>
        </div>
        )}
        {/* `mr-auto`: en `row-reverse` el borde de inicio es el derecho; si no hay
            acciones (staff), los filtros se quedan a la izquierda igualmente. */}
        <div className="mr-auto flex flex-wrap items-center gap-2">
        {/* Estado */}
        <CoarseSafeSelect
          value={statusFilter}
          disabled={isSearching}
          onChange={setStatusFilter}
          options={STATUS_FILTER_OPTIONS_DESKTOP}
          className="h-9 rounded-sm border border-[var(--border)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] disabled:cursor-not-allowed disabled:opacity-40"
        />

        {/* Ordenar */}
        <CoarseSafeSelect
          value={`${sortBy}:${sortDir}`}
          disabled={isSearching}
          onChange={(v) => applySortValue(v, setSortBy, setSortDir)}
          options={SORT_FILTER_OPTIONS}
          className="h-9 rounded-sm border border-[var(--border)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] disabled:cursor-not-allowed disabled:opacity-40"
        />
        </div>
        </div>
      </SectionHeader>
      {/* Cabecera compacta (< xl). MISMA gramática que la de escritorio, en una sola
          tarjeta y con los mismos componentes (decidido 10-09-2026, Yago):
            Fila 1 · identidad + buscar + dar de alta:  título · buscador · QR · Nuevo socio
            Fila 2 · filtrar (izq.) / actuar sobre el listado (dcha.):
                     Estado · Ordenar  ………  Subir ZIP · Exportar
          Por debajo de `lg` las filas se apilan; el orden no cambia. */}
      <div className="block xl:hidden mb-3 space-y-3 rounded-md border border-[var(--border)] bg-[var(--card)] p-4">
        {/* Fila 1 */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="shrink-0">
            <h2 className="whitespace-nowrap text-xl font-semibold text-[var(--foreground)]">Listado Socios</h2>
            {resumenSociosCorto && <p className="whitespace-nowrap text-sm text-[var(--muted-foreground)]">{resumenSociosCorto}</p>}
          </div>
          {/* min-w-0: el buscador cede lo que haga falta; los botones nunca se salen. */}
          <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center lg:flex-1">
            <div className="relative min-w-0 flex-1">
              <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]" />
              <input
                value={inputFiltro}
                onChange={handleInput}
                placeholder="Buscar socio"
                className="h-9 w-full rounded-sm border border-[var(--border)] pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ring-selected)]"
              />
            </div>
            <div className="flex gap-2 sm:shrink-0">
              <Button
                variant="outline"
                onClick={() => setQrModalOpen(true)}
                title="QR Preinscripción"
                aria-label="QR Preinscripción"
                className="shrink-0 px-3"
              >
                <HiOutlineQrcode className="text-base" /> QR
              </Button>
              <Button className="flex-1 sm:flex-none" onClick={() => setActiveModal(true)}>
                Nuevo socio
              </Button>
            </div>
          </div>
        </div>

        {/* Fila 2: filtros a la izquierda, acciones sobre el listado a la derecha.
            Cuando no caben en una línea, las acciones van ENCIMA de los filtros,
            nunca debajo (Yago, 10-09-2026): por eso van primero en el DOM y la fila
            es `row-reverse`. En móvil estrecho, las dos acciones a mitades. */}
        <div className="flex flex-col gap-3 lg:flex-row-reverse lg:items-center lg:justify-between">
          {puedeExportarImportar && (
          <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="px-3">
              Subir ZIP
            </Button>
            <Button
              variant="outline"
              onClick={() => downloadSociosCSV(venueID, statusFilter, token)}
              disabled={isSearching}
              title={isSearching ? 'Sal de la búsqueda para exportar por estado' : 'Descargar CSV de los socios filtrados'}
              className="px-3"
            >
              Exportar
            </Button>
          </div>
          )}
          <div className="flex flex-wrap items-center gap-2 lg:mr-auto">
            <CoarseSafeSelect
              id="socios-estado"
              value={statusFilter}
              disabled={isSearching}
              onChange={setStatusFilter}
              options={STATUS_FILTER_OPTIONS_DESKTOP}
              className="h-9 rounded-sm border border-[var(--border)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] disabled:cursor-not-allowed disabled:opacity-40"
            />
            <CoarseSafeSelect
              id="socios-orden"
              value={`${sortBy}:${sortDir}`}
              disabled={isSearching}
              onChange={(v) => applySortValue(v, setSortBy, setSortDir)}
              options={SORT_FILTER_OPTIONS}
              className="h-9 rounded-sm border border-[var(--border)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] disabled:cursor-not-allowed disabled:opacity-40"
            />
          </div>
        </div>
        {isSearching && (
          <p className="text-xs text-[var(--muted-foreground)]">El orden y el estado no se aplican mientras buscas.</p>
        )}
      </div>
      {/* Hidden File Input for Bulk Upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleBulkUpload}
        accept=".zip"
        style={{ display: 'none' }}
      />

      {/* Solo cuando se ha llegado al límite del plan. La cifra y las plazas van
          bajo el título; el aviso de la importación masiva, en su panel
          (decidido 10-09-2026). */}
      {sociosCap?.max_socios != null && atSociosCap && (
        <div className="rounded-lg px-4 py-3 text-sm border bg-amber-100 text-amber-900 border-amber-300">
          <button
            type="button"
            onClick={() => navigate('/settings')}
            className="font-medium underline underline-offset-2 hover:no-underline text-left"
          >
            Límite de socios alcanzado. Ir a Configuración para mejorar el plan.
          </button>
        </div>
      )}

      <BulkActionsBar
        selectedCount={selectedUserIds.length}
        onClear={clearSelectedUsers}
        onSelectAll={selectVisibleUsers}
        onDelete={handleBulkDelete}
        deleteLabel="Eliminar socios"
        disabled={isBulkDeleting}
      />

      {/* Vista de tarjetas para móvil */}
      <div className="block xl:hidden space-y-3">
        {mostrarUsuarios.length === 0 ? (
          // Mientras carga NO se pinta el estado vacío: la lista se queda vacía un
          // instante al limpiar el filtro, al buscar y al paginar, y en ese hueco
          // se colaba "Todavía no hay socios" en un club con 23 (vídeo del
          // usuario, 18-ago). Medio segundo diciéndole al staff que se ha quedado
          // sin socios es medio segundo de susto gratis.
          loadingUsers ? null : renderSociosEmptyState()
        ) : (
          mostrarUsuarios.map(user => {
            const imageUrl = getImageUrl(getClientProfileImagePath(user.profile), {
              cacheKey: getImageCacheKey('client', user.id),
            });

            const displayName = user.profile?.first_name && user.profile?.last_name
              ? `${user.profile.first_name} ${user.profile.last_name}`
              : (user.email || 'Socio');

            return (
              <div
                key={user.id}
                onClick={() => openClientDetail(user)}
                className="cursor-pointer rounded-md border border-[var(--border)] bg-[var(--card)] p-4 transition-colors hover:border-[var(--border-hover)]"
              >
                <div className="mb-3 flex justify-end">
                  <input
                    type="checkbox"
                    checked={selectedUserIds.some((id) => String(id) === String(user.id))}
                    onChange={() => toggleSelectedUser(user.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 accent-[var(--primary)]"
                  />
                </div>
                {/* Header con imagen y nombre */}
                <div className="flex items-center gap-3 mb-4 group">
                  {/* La misma foto que en la tabla: al tocarla, la grande; sin foto, el zorro. */}
                  <MemberPhoto path={imageUrl} name={displayName} className={FOTO_DE_TARJETA} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-900 truncate text-base group-hover:text-emerald-600 transition-colors">
                        {displayName}
                      </span>
                      <CaducadoChip socio={user} />
                    </div>
                    {user.email && <div className="text-xs text-gray-500 truncate mt-0.5">{user.email}</div>}
                  </div>
                </div>

                {/* Información del socio */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 mb-0.5">Nº Socio</span>
                      <span className="font-semibold text-gray-900">{user.legacy_member_id || user.membership_id || '-'}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 mb-0.5">Saldo</span>
                      <span className={`font-bold text-base ${balanceToneClass(user.profile?.credit_balance)}`}>{balanceListLabel(user.profile?.credit_balance)}</span>
                    </div>
                    {/* La caducidad NO estaba en la tarjeta, solo en la tabla ancha
                        (hidden xl:block), que muchas pantallas no llegan a ver
                        (reporte del usuario, 17-ago: "en lo más ancho no veo fecha
                        ninguna"). En rojo cuando ya pasó. */}
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500 mb-0.5">Caducidad</span>
                      {(() => {
                        const exp = user.profile?.expiration_date ? new Date(user.profile.expiration_date) : null;
                        return (
                          <span className={`font-semibold ${estaCaducado(user) ? 'text-red-600' : 'text-gray-900'}`}>
                            {exp ? exp.toLocaleDateString('es-ES') : '—'}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Acciones de la tarjeta.
                    Esta vista es la que sale en iPad vertical y en cualquier pantalla por
                    debajo de 1280 px — o sea, la que usa medio club. Hasta ahora su ÚNICA
                    acción era un botón rojo enorme de Eliminar: para ver la ficha o tocar el
                    saldo había que acertarle a la foto, y borrar un socio no tiene vuelta
                    atrás (es físico y no queda ni rastro en la auditoría). Ahora las tres
                    acciones de la fila están también aquí, y la de borrar deja de ser la más
                    llamativa de la tarjeta. */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openClientDetail(user);
                    }}
                    className="flex flex-1 h-10 items-center justify-center gap-2 rounded-sm bg-[var(--primary)] px-3 text-sm font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-[0.88]"
                  >
                    <HiOutlineEye className="text-base" />
                    <span>Ver perfil</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedClient(user);
                      setUser_id(user.id);
                      setCreditModal(true);
                    }}
                    className="flex flex-1 h-10 items-center justify-center gap-2 rounded-sm border border-[var(--border)] px-3 text-sm font-medium text-[var(--foreground)] transition-colors hover:bg-[var(--surface-subtle)]"
                  >
                    <HiOutlineCreditCard className="text-base" />
                    <span>Saldo</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(user.id);
                    }}
                    aria-label={`Eliminar ${displayName}`}
                    title="Eliminar socio"
                    className="flex h-10 w-11 flex-shrink-0 items-center justify-center rounded-sm border border-[var(--destructive-border)] text-[var(--destructive)] transition-colors hover:bg-[var(--destructive-bg)]"
                  >
                    <FaDeleteLeft className="text-base" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Vista de tabla para desktop */}
      <div className="hidden xl:block overflow-x-auto bg-white rounded-lg shadow border">
        <table className="min-w-full text-sm text-[var(--foreground)]">
          <TableHead>
            <Th className="w-10"><span className="sr-only">Seleccionar</span></Th>
            <Th>Socio</Th>
            <Th>Nº Socio</Th>
            <Th>Caducidad</Th>
            <Th>Saldo</Th>
            <Th>Estado</Th>
            <Th align="right">Acciones</Th>
          </TableHead>
          <tbody>
            {mostrarUsuarios.map(user => {
              const imageUrl = getImageUrl(getClientProfileImagePath(user.profile), {
                cacheKey: getImageCacheKey('client', user.id),
              });

              const displayName = user.profile?.first_name && user.profile?.last_name
                ? `${user.profile.first_name} ${user.profile.last_name}`
                : (user.email || 'Socio');

              return (
                // El clic que abre la ficha vive en la FILA, no en el recuadro del nombre.
                // Con ratón se acertaba porque apuntas al nombre; con el dedo en una tabla
                // ancha de iPad, la mayoría de los toques caían fuera y no pasaba nada
                // (reporte CRUO 24-08). La casilla y el ⋮ paran la propagación.
                <Tr
                  key={user.id}
                  className="cursor-pointer"
                  onClick={() => openClientDetail(user)}
                >
                  <Td>
                    <input
                      type="checkbox"
                      onClick={(e) => e.stopPropagation()}
                      checked={selectedUserIds.some((id) => String(id) === String(user.id))}
                      onChange={() => toggleSelectedUser(user.id)}
                      className="h-4 w-4 accent-[var(--primary)]"
                    />
                  </Td>
                  <Td>
                    <div className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                      {/* La misma foto que en Entradas (11-09): cuadrado redondeado y, al pasar
                          el ratón o tocar, la foto grande. Un solo componente para las tres listas. */}
                      <MemberPhoto
                        path={imageUrl}
                        name={displayName}
                        className={FOTO_DE_FILA}
                      />
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{displayName}</div>
                        {user.email && <div className="text-xs text-gray-500 truncate">{user.email}</div>}
                      </div>
                    </div>
                  </Td>
                  <Td>
                    <span className="font-medium">{user.legacy_member_id || user.membership_id || '-'}</span>
                  </Td>
                  <Td>
                    {(() => {
                      const exp = user.profile?.expiration_date ? new Date(user.profile.expiration_date) : null;
                      const expired = exp ? exp < new Date(new Date().toDateString()) : false;
                      return <span className={expired ? 'text-red-600 font-medium' : 'text-gray-600'}>{exp ? exp.toLocaleDateString('es-ES') : '—'}</span>;
                    })()}
                  </Td>
                  <Td>
                    {(() => {
                      const bal = user.profile?.credit_balance;
                      return <span className={`font-semibold ${balanceToneClass(bal)}`}>{balanceListLabel(bal)}</span>;
                    })()}
                  </Td>
                  <Td>
                    {(() => {
                      const exp = user.profile?.expiration_date ? new Date(user.profile.expiration_date) : null;
                      const expired = exp ? exp < new Date(new Date().toDateString()) : false;
                      return expired
                        ? <span className="inline-block rounded-full bg-amber-100 text-amber-700 text-xs font-medium px-2.5 py-1">Caducado</span>
                        : <span className="inline-block rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium px-2.5 py-1">Activo</span>;
                    })()}
                  </Td>
                  <Td align="right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const btn = e.currentTarget;
                        setActionMenu((prev) => (prev?.user?.id === user.id ? null : { user, anchorEl: btn }));
                      }}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                      title="Acciones"
                    >
                      <HiDotsVertical className="text-lg" />
                    </button>
                  </Td>
                </Tr>
              );
            })}
            {mostrarUsuarios.length === 0 && !loadingUsers && (
              <tr>
                <Td colSpan={7} className="p-0">
                  {renderSociosEmptyState()}
                </Td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      {/* Menú de acciones de fila (⋮) — MenuFlotante mide la altura real y gira si no cabe */}
      {actionMenu && (
        <MenuFlotante anchorEl={actionMenu.anchorEl} onClose={closeActionMenu} className="min-w-[180px]">
          <button
            onClick={() => { const u = actionMenu.user; setActionMenu(null); openClientDetail(u); }}
            className="flex h-8 w-full items-center gap-2.5 rounded-sm px-3 text-left text-sm text-[var(--sidebar-foreground)] transition-colors hover:bg-[var(--sidebar-hover)]"
          >
            <HiOutlineEye className="text-base text-[var(--muted-foreground)]" /> Ver perfil
          </button>
          <button
            onClick={() => { const u = actionMenu.user; setActionMenu(null); setSelectedClient(u); setUser_id(u.id); setCreditModal(true); }}
            className="flex h-8 w-full items-center gap-2.5 rounded-sm px-3 text-left text-sm text-[var(--sidebar-foreground)] transition-colors hover:bg-[var(--sidebar-hover)]"
          >
            <HiOutlineCreditCard className="text-base text-[var(--muted-foreground)]" /> Saldo
          </button>
          <div className="my-1 border-t border-[var(--border)]" />
          <button
            onClick={() => { const id = actionMenu.user.id; setActionMenu(null); handleDelete(id); }}
            className="flex h-8 w-full items-center gap-2.5 rounded-sm px-3 text-left text-sm text-[var(--destructive)] transition-colors hover:bg-[var(--destructive-bg)]"
          >
            <HiOutlineTrash className="text-base" /> Eliminar
          </button>
        </MenuFlotante>
      )}

      {/* Pagination */}
      <div className="flex flex-col lg:flex-row justify-between items-center gap-3 mt-4">
        <div className="text-sm text-gray-600">
          {totalPages != null ? (
            <>Página {paginaActual} de {totalPages} · {totalCount} socio{totalCount !== 1 ? 's' : ''}</>
          ) : (
            <>{users.length} socios en esta página{hasMore ? " · hay más" : ""}</>
          )}
          {loadingUsers && <span className="text-gray-400"> …</span>}
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPaginaActual(p => Math.max(p - 1, 1))} disabled={paginaActual === 1 || loadingUsers}>
            Anterior
          </Button>

          {/* Números de página (solo cuando conocemos el total) */}
          {totalPages != null ? (
            pageWindow.map((p, i) =>
              p === '…' ? (
                <span key={`gap-${i}`} className="px-2 text-[var(--muted-foreground)] select-none">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => goToPage(p)}
                  disabled={loadingUsers}
                  aria-current={p === paginaActual ? 'page' : undefined}
                  className={`min-w-[2.25rem] h-8 px-3 rounded-sm text-sm font-medium transition-colors disabled:cursor-not-allowed ${
                    p === paginaActual
                      ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
                      : 'bg-[var(--secondary)] text-[var(--foreground)] hover:opacity-[0.85]'
                  }`}
                >
                  {p}
                </button>
              )
            )
          ) : (
            <span className="px-4 py-2 text-sm font-medium text-[var(--muted-foreground)]">
              Página {paginaActual}
            </span>
          )}

          <Button variant="outline" size="sm" onClick={() => setPaginaActual(p => p + 1)} disabled={(totalPages != null ? paginaActual >= totalPages : !hasMore) || loadingUsers}>
            Siguiente
          </Button>

          {/* Ir a página N */}
          {totalPages != null && totalPages > 1 && (
            <form onSubmit={handleGotoSubmit} className="flex items-center gap-1 ml-1">
              <label htmlFor="goto-page" className="text-sm text-[var(--muted-foreground)] whitespace-nowrap">Ir a</label>
              <input
                id="goto-page"
                type="number"
                min={1}
                max={totalPages}
                value={gotoPageInput}
                onChange={(e) => setGotoPageInput(e.target.value)}
                placeholder={String(paginaActual)}
                className="w-16 h-9 px-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
              <button
                type="submit"
                disabled={loadingUsers || gotoPageInput === ''}
                className="px-3 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              >
                Ir
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Modals */}
      {qrModalOpen && <QRModal venueId={venueID} token={token} onClose={() => setQrModalOpen(false)} />}
      <CustomerModal
        active={activeModal}
        handleModal={() => {
          const wasOpen = activeModal;
          setActiveModal(v => !v);
          setUser_id(null); // Limpiar ID al cerrar
          // Recargar datos cuando se cierra el modal (después de crear o actualizar)
          if (wasOpen) {
            fetchPage(page, inputFiltro);
          }
        }}
        token={token}
        id={user_id}
        clientData={clientToEdit}
        shopId={venueID}
        setErrorMessage={setErrorMessage}
        onClientCreated={handleAutoEntryForNewClient}
        onClientUpdated={handleClientUpdated}
      />
      <PurchaseModal active={purchaseModal} handlePurchaseModal={() => setPurchaseModal(v => !v)} user_id={user_id} setErrorMessage={setErrorMessage} costLine={selectedClient?.profile?.cost_line ?? clientToEdit?.profile?.cost_line ?? null} />
      <AddCashModal
        active={creditModal}
        handleAddCashModal={() => setCreditModal(false)}
        token={token}
        user_id={user_id}
        venueID={venueID}
        setErrorMessage={setErrorMessage}
        currentBalance={selectedClient?.profile?.credit_balance ?? clientToEdit?.profile?.credit_balance ?? 0}
        memberName={(() => {
          const m = selectedClient ?? clientToEdit;
          const n = `${m?.profile?.first_name || ''} ${m?.profile?.last_name || ''}`.trim();
          return n || undefined;
        })()}
        memberNumber={socioNum(selectedClient ?? clientToEdit) ?? undefined}
        memberStatus="Activo"
        memberImageUrl={(() => {
          const m = selectedClient ?? clientToEdit;
          const path = m?.profile?.documents_path || m?.profile?.image_path;
          return path ? getImageUrl(path) : undefined;
        })()}
        onCashAdded={(id, newBal) => { // refresh data on success
          fetchPage(page, inputFiltro);
          setCreditModal(false);
          // Cerrar también el detailModal si está abierto
          if (detailModal) {
            setDetailModal(false);
            setSelectedClient(null);
          }
        }}
      />
      {modalEditSocios && <SociosModal key={`socio-${user_id}-${Date.now()}`} email={users.find(u => u.id === user_id)?.email} numeroSocio={user_id} userId={user_id} handleBotonModalEditarSociosSalir={() => setModalEditSocios(false)}>Datos del socio</SociosModal>}

      {/* «¿Eliminar socio?»: el ConfirmDialog del sistema (Club_Alert dialog), no un
          modal propio con capa, sombra y radio a mano (auditoría de UI, UI-010). */}
      <ConfirmDialog
        active={confirmDelete != null}
        title="¿Eliminar socio?"
        description={`Se eliminará ${confirmDelete?.profile?.first_name ? `${confirmDelete.profile.first_name} ${confirmDelete.profile.last_name || ''}`.trim() : 'el socio'} y todo su registro.`}
        note="Esta acción no se puede deshacer."
        confirmLabel="Eliminar socio"
        tone="danger"
        onConfirm={() => { const id = confirmDelete.id; setConfirmDelete(null); performDelete(id); }}
        onCancel={() => setConfirmDelete(null)}
      />

      <ClientDetailModal
        active={detailModal}
        handleModal={() => {
          setDetailModal(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
        token={token}
        venueID={venueID}
        isClientInVenue={usersInVenue.some((user) => String(user.id) === String(selectedClient?.id))}
        onProfilePatched={(clientId, cambios) => {
          const patch = (u) => (String(u.id) === String(clientId) ? { ...u, profile: { ...(u.profile || {}), ...cambios } } : u);
          setUsers((prev) => prev.map(patch));
          setSelectedClient((prev) => (prev && String(prev.id) === String(clientId) ? patch(prev) : prev));
        }}
        onNotesSaved={(clientId, cambios) => {
          const patch = (u) =>
            String(u.id) === String(clientId)
              ? { ...u, profile: { ...(u.profile || {}), ...cambios } }
              : u;
          setUsers((prev) => prev.map(patch));
          setSelectedClient((prev) =>
            prev && String(prev.id) === String(clientId)
              ? { ...prev, profile: { ...(prev.profile || {}), ...cambios } }
              : prev
          );
        }}
        onBalanceChanged={(clientId, newBalance) => {
          // Una anulación devolvió saldo: la lista y la ficha lo reflejan al momento.
          const patch = (u) =>
            String(u.id) === String(clientId)
              ? { ...u, credit_balance: newBalance, profile: { ...(u.profile || {}), credit_balance: newBalance } }
              : u;
          setUsers((prev) => prev.map(patch));
          setSelectedClient((prev) => (prev ? patch(prev) : prev));
        }}
        onEdit={() => {
          if (selectedClient) {
            setUser_id(selectedClient.id);
            setActiveModal(true);
          }
        }}
        onDelete={() => {
          if (selectedClient) {
            const id = selectedClient.id;
            // Cerrar la ficha ANTES de borrar: si no, queda un modal fantasma
            // sobre un socio que ya no existe (hallazgo del review 16-ago).
            setDetailModal(false);
            setSelectedClient(null);
            performDelete(id);
          }
        }}
        onAddCredit={() => {
          if (selectedClient) {
            setUser_id(selectedClient.id);
            setCreditModal(true);
          }
        }}
      />

      {errorMessage && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{errorMessage}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setErrorMessage("")}
          >
            <span className="text-red-700">×</span>
          </button>
        </div>
      )}

      {/* ── Import Progress Modal ─────────────────────────────────────────── */}
      {showImportProgressModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Importación masiva</h2>
              <button
                onClick={closeImportProgressModal}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
              >×</button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">{importStatusMessage}</p>
              {sociosCap?.max_socios != null && (importPhase === 'uploading' || importPhase === 'processing') && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  La importación se detendrá al alcanzar el límite del plan ({sociosCap.socios_count} / {sociosCap.max_socios} socios).
                </p>
              )}

              {importPhase === 'uploading' && (
                <div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Subiendo ZIP</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[var(--primary)] transition-all duration-200"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {importPhase === 'processing' && (
                <div className="space-y-2">
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 animate-pulse w-full" />
                  </div>
                  {importJobStatus && (
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="bg-green-50 rounded p-2">
                        <div className="font-bold text-green-800">{importJobStatus.created_count || 0}</div>
                        <div className="text-green-700">Procesados</div>
                      </div>
                      <div className="bg-yellow-50 rounded p-2">
                        <div className="font-bold text-yellow-800">{importJobStatus.warning_count || 0}</div>
                        <div className="text-yellow-700">Avisos</div>
                      </div>
                      <div className="bg-red-50 rounded p-2">
                        <div className="font-bold text-red-800">{importJobStatus.error_count || 0}</div>
                        <div className="text-red-700">Errores</div>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Puedes cerrar este panel; la importación continúa en segundo plano.
                    Usa &quot;Ver Log Import&quot; para ver el resultado.
                  </p>
                </div>
              )}

              {importPhase === 'done' && (
                <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-800">
                  {importStatusMessage}
                </div>
              )}

              {importPhase === 'error' && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                  {importStatusMessage}
                </div>
              )}

              <div className="flex justify-between items-center gap-2 pt-2">
                <div>
                  {importPhase === 'processing' && lastImportLogId && (
                    <button
                      type="button"
                      onClick={cancelCurrentImport}
                      className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded text-sm font-semibold"
                    >
                      Cancelar importación
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  {(importPhase === 'done' || importPhase === 'error') && lastImportLogId && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowImportProgressModal(false);
                        openImportLog();
                      }}
                      className="px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded text-sm font-semibold"
                    >
                      Ver log
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={closeImportProgressModal}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm font-semibold"
                  >
                    {importPhase === 'processing' ? 'Cerrar panel' : 'Cerrar'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Import Log Modal ─────────────────────────────────────────────── */}
      {showLogModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-800">📋 Log de Importación</h2>
                {importLog && !importLog.error && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {importLog.timestamp ? new Date(importLog.timestamp).toLocaleString('es-ES') : ''} · ID: {importLog.log_id}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowLogModal(false)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
              >×</button>
            </div>

            {loadingLog && (
              <div className="flex-1 flex items-center justify-center py-16 text-gray-500">
                Cargando log…
              </div>
            )}

            {!loadingLog && importLog?.error && (
              <div className="flex-1 p-6">
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                  <span className="font-semibold">Log no disponible.</span>{' '}
                  {importLog.error}
                </div>
              </div>
            )}

            {!loadingLog && importLog && !importLog.error && (
              <>
                {/* Summary pills */}
                <div className="flex flex-wrap gap-3 p-4 border-b border-gray-100">
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                    ✅ Creados: {importLog.created_count}
                  </span>
                  {importLog.updated_count > 0 && (
                    <span className="px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                      ♻️ Actualizados: {importLog.updated_count}
                    </span>
                  )}
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800">
                    ⚠️ Avisos: {importLog.warning_count}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                    ❌ Errores: {importLog.error_count}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
                    Total filas: {importLog.total_rows}
                  </span>
                  {importLog.csv_total_rows != null && importLog.csv_total_rows !== importLog.total_rows && (
                    <span className="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
                      CSV: {importLog.csv_total_rows} filas
                    </span>
                  )}
                </div>

                {importLog.cap_warning?.message && (
                  <div className="mx-4 mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <div className="font-semibold">Límite del plan alcanzado</div>
                    <p className="mt-1">{importLog.cap_warning.message}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setShowLogModal(false);
                        navigate('/settings');
                      }}
                      className="mt-2 font-medium underline underline-offset-2 hover:no-underline"
                    >
                      Ir a Configuración para mejorar el plan
                    </button>
                  </div>
                )}

                {(importLog.errors_summary || []).length > 0 && !importLog.cap_warning?.message && (
                  <div className="mx-4 mt-4 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-900">
                    <div className="font-semibold mb-1">Resumen de avisos</div>
                    {(importLog.errors_summary || []).slice(0, 5).map((msg, idx) => (
                      <p key={idx} className="mt-1">{msg}</p>
                    ))}
                  </div>
                )}

                {/* Filter tabs */}
                <div className="flex gap-1 px-4 pt-3">
                  {[
                    { key: 'all', label: 'Todas' },
                    { key: 'created', label: 'Creados' },
                    { key: 'updated', label: 'Actualizados' },
                    { key: 'warning', label: 'Con avisos' },
                    { key: 'error', label: 'Errores' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setLogFilter(key)}
                      className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
                        logFilter === key
                          ? 'bg-slate-700 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {/* Rows table */}
                <div className="flex-1 overflow-y-auto px-4 py-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-gray-500 border-b border-gray-200">
                        <th className="py-2 pr-2 font-semibold w-10">#</th>
                        <th className="py-2 pr-2 font-semibold">Estado</th>
                        <th className="py-2 pr-2 font-semibold">Nombre</th>
                        <th className="py-2 pr-2 font-semibold">DNI original</th>
                        <th className="py-2 font-semibold">Avisos / Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applyLogFilter(importLog.rows, logFilter)
                        .map((row, i) => {
                          const hasWarning = (row.warnings && row.warnings.length > 0) || row.status === 'skipped_cap';
                          const isError = row.status === 'error';
                          const isCapSkip = row.status === 'skipped_cap';
                          const isUpdated = row.status === 'updated';
                          return (
                            <tr
                              key={i}
                              className={`border-b border-gray-50 ${
                                isError ? 'bg-red-50' : hasWarning ? 'bg-yellow-50' : isUpdated ? 'bg-blue-50' : ''
                              }`}
                            >
                              <Td className="py-1.5 pr-2 text-gray-400">{row.row}</Td>
                              <Td className="py-1.5 pr-2">
                                <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                                  isError ? 'bg-red-200 text-red-800' :
                                  isCapSkip ? 'bg-amber-200 text-amber-900' :
                                  hasWarning ? 'bg-yellow-200 text-yellow-800' :
                                  isUpdated ? 'bg-blue-200 text-blue-800' :
                                  'bg-green-200 text-green-800'
                                }`}>
                                  {isError ? 'error' : isCapSkip ? 'límite' : hasWarning ? 'aviso' : isUpdated ? 'actualizado' : '✓'}
                                </span>
                              </Td>
                              <Td className="py-1.5 pr-2 text-gray-700">{row.name || '—'}</Td>
                              <Td className="py-1.5 pr-2 font-mono text-gray-500">{row.dni_original || '—'}</Td>
                              <Td className="py-1.5 text-gray-600">
                                {hasWarning && row.warnings.map((w, wi) => (
                                  <div key={wi} className="text-yellow-700">⚠ {w}</div>
                                ))}
                                {row.error && <div className="text-red-700">✗ {row.error}</div>}
                              </Td>
                            </tr>
                          );
                        })
                      }
                    </tbody>
                  </table>
                  {applyLogFilter(importLog.rows, logFilter).length === 0 && (
                    <p className="text-center text-gray-400 py-8">No hay filas con este filtro.</p>
                  )}
                </div>

                {/* Footer: downloads */}
                <div className="p-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-xs text-gray-400">
                    {applyLogFilter(importLog.rows, logFilter).length} fila{applyLogFilter(importLog.rows, logFilter).length !== 1 ? 's' : ''} mostradas
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => downloadFilteredCSV(importLog, logFilter)}
                    >
                      ⬇ Descargar CSV{logFilter !== 'all' ? ` (${({ created: 'creados', updated: 'actualizados', warning: 'avisos', error: 'errores' })[logFilter]})` : ''}
                    </Button>
                    <a
                      href={`data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(importLog, null, 2))}`}
                      download={`import-log-${importLog.log_id}.json`}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-sm font-semibold transition-colors"
                    >
                      ⬇ JSON
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {dialogoConfirmar}
    </div>
  );
}
